import { findAiPage } from "../registry/registry.js";
import { AI_PAGE_GROUP_ROLES, type AiPageGroupRole } from "../registry/schema.js";
import { GUIDE_CARD_LIBRARY, type GuideCard } from "./cards.js";
import type { GuideTrigger } from "./triggers.js";
import { GUIDE_SIGNALS, type GuideSignalKey } from "./signals.js";

/**
 * 🔴🔴 US-AIV2-tourguide-0002 (@UC0360 · TG-INV-1) — TÍN HIỆU NÀO ĐƯỢC NẠP CHO VAI NÀO. **Một bảng khai, loader đọc chính nó.**
 *
 * Trước task này loader quyết "nạp cho vai nào" bằng ba nhánh rải rác (`X_ROLES.includes(seat.role) ? đọc : Promise.resolve(0|false)`),
 * và nhánh "không nạp" trả một GIÁ TRỊ ĐÃ BIẾT. Máy đo `0001` (K05 · K06, 42 ô) đo được hệ quả: thành viên ở nhóm CHƯA có số tài khoản
 * nhận tiền được Người dẫn đường "tin" là nhóm ĐÃ có (`bankAccountMissing = false`), thủ quỹ "tin" nhóm 0 lời xin vào nhóm trong khi
 * màn Thành viên của chính họ thấy 1. "Không đo" giả làm "đã đo = 0/false" — đúng lớp nói-sai mà `unknownSignals` sinh ra để chặn,
 * chỉ đổi cửa vào.
 *
 * Luật của bảng:
 *  1. **Một nguồn.** Loader KHÔNG giữ danh sách vai riêng: vai ngoài `SIGNAL_LOAD_ROLES[k]` ⇒ loader KHÔNG chạy truy vấn của `k` và KHAI
 *     `k` vào `unknownSignals` (`guideSignalsNotLoadedFor`). Không còn đường `Promise.resolve(false|0)` nào cho vai không nạp.
 *  2. **Đủ vai cho mọi thẻ.** Thẻ hiện cho vai R mà đọc tín hiệu `k` (`when[]` hoặc `vars`) ⇒ `k` PHẢI được nạp cho R. Chốt tự khám phá:
 *     `signal-load.test.ts` (nêu TÊN thẻ + vai + tín hiệu khi lệch). Nạp thiếu thì thẻ im ở đúng vai nó sinh ra để nói.
 *  3. **Vai hẹp chỉ khi cửa GUI hẹp.** Ba tín hiệu nạp theo vai của TRANG nút thẻ (đọc từ Sổ, như loader đã làm từ `0111`/`0112`):
 *     lời báo chờ đối chiếu (`PAY-CONFIRM-DECIDE` — `roles-parity.invariants` khoá trang này = `PAYMENT_APPROVER_ROLES` của cửa duyệt) ·
 *     lời xin vào nhóm (`GRP-JOIN-REQUEST`) · tiến độ đợt còn người nhắc được (`DEBT-REMIND`). Vai khác không mở được cửa đọc của chúng
 *     (máy đo: 403/404) ⇒ với vai đó tín hiệu là KHÔNG BIẾT, không phải 0.
 *     `bankAccountMissing` cố ý nạp cho MỌI vai có ghế: cửa đọc `GET /groups/:id/bank-account` mở cho mọi thành viên
 *     (`groups.controller` không gắn `@RequireGroupRoles`) và câu Q1 của `0004` (thành viên còn nợ, nhóm chưa có STK) cần nó.
 *  4. **`"none"` = người hỏi KHÔNG có ghế ở nhóm hiệu lực** (tab toàn cục · người ngoài nhóm). Chỉ họ NGƯỜI DÙNG (đếm theo chính người
 *     hỏi, trước khi biết nhóm), họ LƯỢT (caller truyền) và `role` có câu trả lời ở đó.
 *
 * ⚠️ Bảng là `Record<GuideSignalKey, …>`: thêm tín hiệu vào `GUIDE_SIGNALS` mà không khai dòng ở đây là TS đỏ — không có tín hiệu nào
 * "mặc định nạp" hay "mặc định không nạp" một cách im lặng.
 *
 * Bảng nói chuyện VAI. Còn một lớp "không biết" theo GIÁ TRỊ mà bảng không thay: phép đọc hỏng (`count()` ⇒ `null`) · mốc "đã xem"
 * vắng (`myRoleChanged`/`configChangedCount`) · caller không đếm (`turnsWithoutProgress`) · người đã có nhóm (lời mời) — loader khai
 * từng ca đó tại chỗ đọc, cũng vào `unknownSignals`.
 */
export type GuideSignalLoadRole = AiPageGroupRole | "none";

/** Mọi vai CÓ GHẾ — suy từ Sổ (`AI_PAGE_GROUP_ROLES`), không chép tay. */
export const GUIDE_SEATED_ROLES: readonly AiPageGroupRole[] = Object.freeze([...AI_PAGE_GROUP_ROLES]);
/** Mọi vai + "không ghế" — hàng của tín hiệu biết được ở mọi phạm vi. */
export const GUIDE_SIGNAL_LOAD_ALL_ROLES: readonly GuideSignalLoadRole[] = Object.freeze([...AI_PAGE_GROUP_ROLES, "none" as const]);

/**
 * Vai của trang nút (đọc từ Sổ). Trang vắng ⇒ `[]` ⇒ tín hiệu không nạp cho ai ⇒ thẻ im (fail-closed, KHÔNG đoán quyền) — và chốt
 * `signal-load.test.ts` đỏ ngay vì thẻ đọc tín hiệu đó không còn vai nào được nạp.
 */
const rolesOfPage = (pageId: string): readonly GuideSignalLoadRole[] => Object.freeze([...(findAiPage(pageId)?.roles ?? [])]);

export const SIGNAL_LOAD_ROLES: Readonly<Record<GuideSignalKey, readonly GuideSignalLoadRole[]>> = Object.freeze({
  // ——— họ NGƯỜI DÙNG: đếm theo chính người hỏi, TRƯỚC khi biết nhóm hiệu lực (đúng cả ở tab toàn cục).
  hasAnyGroup: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  groupCount: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  myJoinPendingCount: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  myInviteCount: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  // ——— họ LƯỢT: caller truyền (sự thật của chính lượt này, DB không lưu).
  turnFailed: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  turnsWithoutProgress: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  askedGroupNoSeat: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  // ——— kết quả của CHÍNH phép phân giải ghế: "không ghế" cũng là một câu trả lời.
  role: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  // ——— họ NHÓM: mọi vai có ghế.
  groupName: GUIDE_SEATED_ROLES,
  fundCount: GUIDE_SEATED_ROLES,
  memberCount: GUIDE_SEATED_ROLES,
  openCollectionCount: GUIDE_SEATED_ROLES,
  myUnpaidDueCount: GUIDE_SEATED_ROLES,
  myClaimedDueCount: GUIDE_SEATED_ROLES,
  // G-4 GUIDE-FROZEN-FUND — cùng lần đọc với `myClaimedDueCount` (mọi vai có ghế báo đã chuyển được cho suất mình).
  myClaimedAllFrozen: GUIDE_SEATED_ROLES,
  myOpenDuesSettled: GUIDE_SEATED_ROLES,
  unfinishedTaskCount: GUIDE_SEATED_ROLES,
  // US-AIV2-tourguide-0007a (@UC0365): phiếu dở của CHÍNH người hỏi ở nhóm hiệu lực — mọi vai có ghế mở được phiếu ghi của mình.
  unfinishedTaskMoney: GUIDE_SEATED_ROLES,
  unfinishedOtherCount: GUIDE_SEATED_ROLES,
  myRoleChanged: GUIDE_SEATED_ROLES,
  configChangedCount: GUIDE_SEATED_ROLES,
  // 🔴 MỌI vai có ghế (luật 3): cửa đọc STK mở cho mọi thành viên; `0004` cần nó cho câu Q1 của thành viên.
  bankAccountMissing: GUIDE_SEATED_ROLES,
  // 🔴 US-AIV2-tourguide-0004 (@UC0360) — tín hiệu của biến thể câu. Cách lời tự khai vào sổ + STK đổi: mọi vai có ghế (cửa đọc cấu hình quỹ
  // `GET /funds/:id/config` và STK mở cho mọi thành viên). Lời mời của nhóm: chỉ vai GỬI được lời mời (trang `ONB-ADD-MEMBERS`).
  myReportAutoConfirm: GUIDE_SEATED_ROLES,
  configBankChanged: GUIDE_SEATED_ROLES,
  configOtherChanged: GUIDE_SEATED_ROLES,
  groupPendingInviteCount: rolesOfPage("ONB-ADD-MEMBERS"),
  // họ LƯỢT (caller truyền), như `turnsWithoutProgress`.
  stuckTurnKind: GUIDE_SIGNAL_LOAD_ALL_ROLES,
  // US-AIV2-tourguide-0011 (@UC0371): cửa «Việc của tôi» mở cho MỌI vai có ghế (`GET /v1/me/action-items` chỉ gác đăng nhập).
  myOpenVoteCount: GUIDE_SEATED_ROLES,
  myPayOnBehalfCount: GUIDE_SEATED_ROLES,
  // QĐ PO 04/10 (LOW-2): cấu hình quỹ đọc được bởi mọi thành viên (`GET /funds/:id/config` trả `payOnBehalfCreatesDebt`).
  myPayOnBehalfCreatesDebt: GUIDE_SEATED_ROLES,
  // ——— họ NHÓM, vai hẹp = vai của trang nút thẻ (luật 3).
  pendingConfirmations: rolesOfPage("PAY-CONFIRM-DECIDE"),
  // AI-SO-DRIFT-1012 (@UC0217): cửa duyệt chi gác O/A/T (`listPendingDecisionForApprover` — `PAYMENT_APPROVER_ROLES`) = vai trang EXP-APPROVE.
  pendingExpenseApprovals: rolesOfPage("EXP-APPROVE"),
  pendingJoinRequests: rolesOfPage("GRP-JOIN-REQUEST"),
  openCollectionRemindable: rolesOfPage("DEBT-REMIND"),
  openCollectionRemindedRecently: rolesOfPage("DEBT-REMIND"),
});

/** Vai của người hỏi theo cách bảng đọc: `null` (không ghế / chưa thuộc nhóm) ⇒ `"none"`. */
export function guideSignalLoadRole(role: AiPageGroupRole | null): GuideSignalLoadRole {
  return role ?? "none";
}

/** Loader có nạp tín hiệu `k` cho vai `role` không — câu hỏi DUY NHẤT loader hỏi trước khi chạy truy vấn của `k`. */
export function guideSignalLoadsFor(k: GuideSignalKey, role: AiPageGroupRole | null): boolean {
  return SIGNAL_LOAD_ROLES[k].includes(guideSignalLoadRole(role));
}

/**
 * Mọi tín hiệu KHÔNG nạp cho vai `role` — loader khai chúng vào `unknownSignals` (thay cho các danh sách họ-tín-hiệu chép tay cũ).
 * Thứ tự = thứ tự `GUIDE_SIGNALS` (tất định).
 */
export function guideSignalsNotLoadedFor(role: AiPageGroupRole | null): readonly GuideSignalKey[] {
  return (Object.keys(GUIDE_SIGNALS) as GuideSignalKey[]).filter((k) => !guideSignalLoadsFor(k, role));
}

/**
 * 🔴 US-AIV2-tourguide-0011 (review #1215 MEDIUM-1) — trigger này có thẻ nào ĐỌC một trong `signals` không. Loader dùng để chỉ chạy cửa NẶNG
 * («Việc của tôi») cho lượt có thể nói tới nó — tự khám phá từ thư viện, không danh sách trigger chép tay.
 */
export function guideTriggerReadsSignals(trigger: GuideTrigger, signals: readonly GuideSignalKey[], library: readonly GuideCard[] = GUIDE_CARD_LIBRARY): boolean {
  return library.some((c) => c.triggers.includes(trigger) && guideCardSignals(c).some((s) => signals.includes(s)));
}

/** Tín hiệu một thẻ ĐỌC: điều kiện `when[]` + chỗ trống số `vars` — đúng tập `signalsKnown()` của `journey()` soi. */
export function guideCardSignals(card: GuideCard): readonly GuideSignalKey[] {
  return [...new Set<GuideSignalKey>([...card.when.map((p) => p.signal), ...Object.values(card.vars ?? {})])];
}
